import { Fade, type PopperProps, type SxProps, type Theme } from '@mui/material';
import { useAtomValue } from 'jotai';
import { memo, useEffect, useRef, useState } from 'react';
import { hintsAtom } from '@/atoms/hints';
import { usePrefValue } from '@/preferences';
import { HintAnchor, HintAngle, HintBody, HintMenuAnimation, HintTarget } from './Hint.styles';
import { HintContent, type IHintContent } from './HintContent';

interface IHint {
    mode?: 'menu';
    anchorSx?: SxProps<Theme>;
}

export const Hint = memo(
    ({
        children,
        block,
        hint,
        mode,
        size,
        anchorSx,
        ...rest
    }: Omit<PopperProps, 'open' | 'children'> & IHint & IHintContent & { children?: React.ReactNode }) => {
        const anchorEl = useRef<HTMLDivElement>(undefined);

        const side_bar = usePrefValue('side_bar');

        const [anchorSize, setAnchorSize] = useState<{
            width: number;
            height: number;
        }>();

        const hintState = useAtomValue(hintsAtom);
        const show = hintState.item === hint.name;

        useEffect(() => {
            if (anchorEl.current) {
                const anchor = anchorEl.current.getBoundingClientRect();
                setAnchorSize({ width: anchor.width, height: anchor.height });
            }
        }, []);

        return (
            <>
                <HintTarget>
                    <HintAnchor ref={anchorEl} variant={mode} sx={anchorSx} sideBar={side_bar}>
                        {mode === 'menu' && show && <HintMenuAnimation />}
                    </HintAnchor>
                    {children}
                </HintTarget>
                <HintBody id={`${block}-${hint.name}`} open={show} anchorEl={anchorEl.current} placement="right-start" transition {...rest}>
                    {({ TransitionProps, placement }) => (
                        <Fade {...TransitionProps}>
                            <div className="relative">
                                <HintContent block={block} hint={hint} size={size} />
                                <HintAngle blockSize={anchorSize} placement={placement} />
                            </div>
                        </Fade>
                    )}
                </HintBody>
            </>
        );
    },
);
